/**
 * ARIA Session Timeout
 *
 * Two behaviors, both driven by one activity clock:
 *  - Idle for IDLE_TIMEOUT_MS with no interaction -> signed out, sent to
 *    /login with an explanation.
 *  - Active within that window -> the JWT is silently refreshed in the
 *    background every REFRESH_INTERVAL_MS, so a working user is never
 *    signed out mid-task by the token's own fixed expiry.
 *
 * Mounted once, only while authenticated — see RequireAuth.tsx.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useApolloClient } from '@apollo/client';
import { useAppDispatch, logout } from '../store';
import { getAuthToken, setAuthToken, clearAuthToken } from '../config/apollo';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // sign out after this long with no interaction
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // renew the token this often while active
const TICK_MS = 30 * 1000; // how often to check the two timers above
const ACTIVITY_THROTTLE_MS = 5 * 1000; // ignore activity events more frequent than this

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
];

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken {
    refreshToken {
      token
    }
  }
`;

export function useSessionTimeout(): void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();

  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());

  useEffect(() => {
    const recordActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > ACTIVITY_THROTTLE_MS) {
        lastActivityRef.current = now;
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, recordActivity, { passive: true }),
    );

    const tick = async () => {
      const now = Date.now();
      const idleFor = now - lastActivityRef.current;

      if (idleFor >= IDLE_TIMEOUT_MS) {
        clearAuthToken();
        dispatch(logout('You were signed out after 15 minutes of inactivity. Please log in again.'));
        navigate('/login', { replace: true });
        return;
      }

      if (now - lastRefreshRef.current >= REFRESH_INTERVAL_MS && getAuthToken()) {
        try {
          const { data } = await client.mutate({ mutation: REFRESH_TOKEN_MUTATION });
          if (data?.refreshToken?.token) {
            setAuthToken(data.refreshToken.token);
            lastRefreshRef.current = now;
          }
        } catch {
          // A transient network/server hiccup on a background refresh
          // shouldn't sign the user out — the idle timer and, failing that,
          // the next real request's own auth check are the actual safety
          // nets. Just try again on the next tick.
        }
      }
    };

    const intervalId = window.setInterval(tick, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, recordActivity));
      window.clearInterval(intervalId);
    };
  }, [dispatch, navigate, client]);
}
